import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, FileText, Calendar, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STEPS = [
  { key: "application_received", label: "Application Received" },
  { key: "interview_scheduled", label: "Interview Scheduled" },
  { key: "interview_complete", label: "Interview Complete" },
  { key: "offer_extended", label: "Offer Extended" },
  { key: "offer_accepted", label: "Offer Accepted" },
  { key: "onboarding", label: "Onboarding" },
  { key: "employee", label: "Welcome to the Team" },
];

export default function Track() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hiring/applications/track`, {
        params: { code: code.trim().toUpperCase(), email: email.trim().toLowerCase() },
      });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not find an application with that code and email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] py-6 md:py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <header className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Leynix
          </div>
          <h1 className="heading-display">Track your application</h1>
          <p className="text-muted-foreground text-sm mt-1">Enter your tracking code and email to view status.</p>
        </header>

        {!data && (
          <form onSubmit={lookup} className="surface p-6 md:p-8 space-y-4" data-testid="track-form">
            <div>
              <label className="text-sm font-medium block mb-1.5">Tracking code</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} required className="input-base font-mono uppercase" placeholder="APP-XXXXXX-XXXX" data-testid="track-code" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Email used to apply</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input-base" data-testid="track-email" />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="track-error">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="track-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Look up application"}
            </button>
          </form>
        )}

        {data && (
          <div className="space-y-5">
            <div className="surface p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="overline">Applicant</div>
                  <div className="font-semibold mt-0.5">{data.first_name} {data.last_name}</div>
                </div>
                <div className="text-right">
                  <div className="overline">Tracking</div>
                  <div className="font-mono text-sm mt-0.5">{data.tracking_code}</div>
                </div>
              </div>
              <div className="mt-5">
                <div className="overline mb-3">Current Status</div>
                <ol className="space-y-3">
                  {STATUS_STEPS.map((s, i) => {
                    const currentIdx = STATUS_STEPS.findIndex((x) => x.key === data.status);
                    const isDone = i <= currentIdx;
                    const isCurrent = i === currentIdx;
                    return (
                      <li key={s.key} className="flex items-center gap-3" data-testid={`track-step-${s.key}`}>
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isDone ? "text-white" : "bg-secondary"}`}
                          style={isDone ? { backgroundColor: "hsl(var(--primary))" } : {}}
                        >
                          {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="text-xs text-muted-foreground">{i + 1}</span>}
                        </div>
                        <div className={`text-sm ${isCurrent ? "font-semibold" : isDone ? "" : "text-muted-foreground"}`}>{s.label}</div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>

            {data.interviews?.length > 0 && (
              <div className="surface p-6">
                <h3 className="heading-section mb-3">Interviews</h3>
                {data.interviews.map((iv) => (
                  <div key={iv.id} className="border border-border rounded-lg p-3 flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{new Date(iv.scheduled_at).toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">{iv.interview_type.replace("_", " ")}{iv.location ? ` · ${iv.location}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="surface p-6">
              <h3 className="heading-section mb-3">Documents on file ({data.documents.length})</h3>
              {data.documents.length === 0 ? (
                <div className="text-sm text-muted-foreground">No documents uploaded yet.</div>
              ) : (
                <div className="space-y-2">
                  {data.documents.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 border border-border rounded-lg p-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div className="text-sm flex-1 capitalize">{d.document_type.replace(/_/g, " ")}</div>
                      {d.will_bring_to_office ? <span className="pill-warning">Will bring</span> : <span className="pill-success">Uploaded</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="text-center">
              <button onClick={() => { setData(null); setCode(""); setEmail(""); }} className="btn-ghost text-sm" data-testid="track-reset">
                Look up another application
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
